/**
 * Generates a human-readable label describing the comparison period between two dates.
 *
 * @param filterDateFrom - The start date of the current period
 * @param compareDateFrom - The start date of the comparison period
 * @returns A string label describing the comparison period
 *
 * @example
 * const label = getComparisonLabel('2024-10-16', '2024-10-15');
 * // label will be 'vs previous day'
 */
export const getComparisonLabel = (
  filterDateFrom: string,
  compareDateFrom: string
): string => {
  // Parse dates
  const filterDate = new Date(filterDateFrom);
  const compareDate = new Date(compareDateFrom);
  const today = new Date();

  // If either date is in the future, return a generic label
  if (filterDate > today || compareDate > today) {
    console.log('Future date detected');
    return 'vs comparison period';
  }

  // Calculate the difference in days between the two dates
  const diffInDays = Math.abs(
    (filterDate.getTime() - compareDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Check if dates are consecutive or same day
  const daysBetween = Math.abs(
    Math.floor(
      (filterDate.getTime() - compareDate.getTime()) / (1000 * 60 * 60 * 24)
    )
  );

  // Only use "vs previous day" if the dates are exactly 1 day apart
  if (daysBetween === 1) {
    return 'vs previous day';
  }

  // If dates are the same day or less than 1 day apart
  if (daysBetween === 0) {
    return 'vs same day';
  }

  // Standard time periods
  if (diffInDays >= 7 && diffInDays <= 8) return 'vs previous 7 days';
  if (diffInDays >= 28 && diffInDays <= 31) return 'vs previous 30 days';
  if (diffInDays >= 89 && diffInDays <= 92) return 'vs previous 3 months';
  if (diffInDays >= 350 && diffInDays <= 380) return 'vs previous year';

  // For non-standard ranges
  const result = `vs previous ${Math.round(diffInDays)} days`;
  return result;
};
